import { Copy, LockKey } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text, TextArea } from "@radix-ui/themes";
import { useCallback, useRef, useState } from "react";

interface PasswordProtectedPdfDialogProps {
  /** The protected PDF filename to render in the message. Null hides the dialog. */
  fileName: string | null;
  /** Called when the user dismisses the dialog. */
  onClose: () => void;
}

const buildEmailTemplate = (fileName: string) =>
  `Hi,

We're trying to upload ${fileName} into Puntt for AI review, but the PDF is password-protected and our tool cannot read encrypted files.

Could you re-export this file without password protection (or share the editable source) and send it back? That will unblock the review.

Thanks!`;

/**
 * Modal shown when a user tries to upload a password-protected PDF.
 * Surfaces a clear, actionable message + a copy-to-clipboard email
 * template the marketing team can send to their agency to request an
 * unrestricted version. Customer-driven: Nestle CSV #4 — Diana asked
 * us to "tell marketing teams up front" rather than failing silently
 * in the review pipeline.
 */
export function PasswordProtectedPdfDialog({
  fileName,
  onClose,
}: PasswordProtectedPdfDialogProps) {
  const open = fileName != null;
  const [copied, setCopied] = useState(false);
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    if (!fileName) return;
    const text = buildEmailTemplate(fileName);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for environments without the modern clipboard API
        // (older browsers, some sandbox iframes). Silently no-op rather
        // than throw — the textarea below is selectable as a last resort.
      }
      setCopied(true);
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
      copyResetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Same fallback rationale — keep the dialog usable.
    }
  }, [fileName]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setCopied(false);
          onClose();
        }
      }}
    >
      <Dialog.Content
        maxWidth="520px"
        data-testid="password-protected-pdf-dialog"
        aria-labelledby="password-protected-pdf-title"
      >
        <Flex align="center" gap="2" mb="2">
          <LockKey size={20} aria-hidden="true" />
          <Dialog.Title id="password-protected-pdf-title" mb="0">
            This PDF is password-protected
          </Dialog.Title>
        </Flex>
        <Dialog.Description size="2" mb="3">
          Puntt can&apos;t open password-protected PDFs, so the AI review
          won&apos;t be able to read <strong>{fileName ?? "this file"}</strong>.
          Ask whoever sent the file (agency, brand team, etc.) for an
          unrestricted version. You can copy the message below and send it
          directly.
        </Dialog.Description>
        <Text
          as="label"
          size="2"
          weight="medium"
          htmlFor="password-protected-pdf-template"
        >
          Suggested message
        </Text>
        <TextArea
          id="password-protected-pdf-template"
          data-testid="password-protected-pdf-template"
          value={fileName ? buildEmailTemplate(fileName) : ""}
          readOnly
          rows={8}
          style={{ marginTop: 6 }}
          aria-label="Suggested email template to request an unrestricted PDF"
        />
        <Flex mt="3" gap="2" justify="end">
          <Button
            data-testid="password-protected-pdf-copy"
            variant="solid"
            onClick={handleCopy}
            aria-label="Copy suggested message to clipboard"
          >
            <Copy size={16} aria-hidden="true" />
            {copied ? "Copied!" : "Copy message"}
          </Button>
          <Button
            data-testid="password-protected-pdf-close"
            variant="soft"
            color="gray"
            onClick={onClose}
          >
            Close
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
